import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarCheck, ExternalLink, Lock, ShieldCheck } from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';
import useInstalledAppView from '../../hooks/useInstalledAppView';
import BrandLockup from './BrandLockup';

const AGE_CONSENT_STORAGE_KEY = 'issac_age_consent';
const AGE_CONSENT_COOKIE_NAME = 'issac_age_consent';
const AGE_CONSENT_VERSION = '2026-05-17';
const AGE_CONSENT_TTL_DAYS = 365;
const AUTH_ONLY_PATHS = new Set(['/login', '/register', '/forgot-password', '/reset-password']);

interface AgeConsentRecord {
  accepted: boolean;
  version: string;
  acceptedAt: string;
  expiresAt: string;
}

const isValidConsentRecord = (record: Partial<AgeConsentRecord> | null): boolean => {
  if (!record?.accepted || record.version !== AGE_CONSENT_VERSION || !record.expiresAt) {
    return false;
  }

  const expiry = new Date(record.expiresAt).getTime();
  return Number.isFinite(expiry) && expiry > Date.now();
};

const readStoredConsent = (): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  try {
    const stored = window.localStorage.getItem(AGE_CONSENT_STORAGE_KEY);
    if (stored && isValidConsentRecord(JSON.parse(stored))) {
      return true;
    }
  } catch {
    try {
      window.localStorage.removeItem(AGE_CONSENT_STORAGE_KEY);
    } catch {
      // Ignore storage failures; the cookie fallback can still work.
    }
  }

  const cookieValue = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${AGE_CONSENT_COOKIE_NAME}=`))
    ?.split('=')[1];

  if (!cookieValue) {
    return false;
  }

  try {
    return isValidConsentRecord(JSON.parse(decodeURIComponent(cookieValue)));
  } catch {
    return false;
  }
};

const persistConsent = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const acceptedAt = new Date();
  const expiresAt = new Date(acceptedAt);
  expiresAt.setDate(expiresAt.getDate() + AGE_CONSENT_TTL_DAYS);

  const record: AgeConsentRecord = {
    accepted: true,
    version: AGE_CONSENT_VERSION,
    acceptedAt: acceptedAt.toISOString(),
    expiresAt: expiresAt.toISOString(),
  };

  const serialized = JSON.stringify(record);

  try {
    window.localStorage.setItem(AGE_CONSENT_STORAGE_KEY, serialized);
  } catch {
    // Some browser privacy modes block localStorage.
  }

  try {
    document.cookie = `${AGE_CONSENT_COOKIE_NAME}=${encodeURIComponent(serialized)}; Max-Age=${AGE_CONSENT_TTL_DAYS * 24 * 60 * 60}; Path=/; SameSite=Lax`;
  } catch {
    // The in-memory accept state still allows the current visit to continue.
  }
};

const AgeConsentGate: React.FC = () => {
  const location = useLocation();
  const { settings } = useSettings();
  const [isAccepted, setIsAccepted] = useState(() => readStoredConsent());
  const [confirmedAdult, setConfirmedAdult] = useState(false);
  const isInstalledAppView = useInstalledAppView();
  const isAuthOnlyPath = AUTH_ONLY_PATHS.has(location.pathname);
  const shouldBypassAgeGate = isAuthOnlyPath && !isInstalledAppView;
  const platformName = settings?.platform_name?.trim() || 'Bid4Babes';
  const platformTagline = settings?.platform_tagline?.trim() || '';

  useEffect(() => {
    if (isAccepted || shouldBypassAgeGate) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAccepted, shouldBypassAgeGate]);

  const handleEnter = () => {
    if (!confirmedAdult) {
      return;
    }

    persistConsent();
    setIsAccepted(true);
  };

  const handleExit = () => {
    window.location.replace('https://www.google.com');
  };

  if (isAccepted || shouldBypassAgeGate) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[9999] isolate flex min-h-[100dvh] items-start justify-center overflow-y-auto bg-slate-50 px-3 py-3 text-slate-950 dark:bg-slate-950 dark:text-white sm:items-center sm:px-5 sm:py-6 lg:px-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-consent-title"
      aria-describedby="age-consent-description"
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-100 via-white to-purple-100 dark:from-emerald-500/20 dark:via-slate-950 dark:to-purple-500/20" />

      <div className="relative flex w-full max-w-[min(100%,40rem)] max-h-[calc(100dvh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-300/50 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/50 sm:max-h-[calc(100dvh-3rem)]">
        <div className="shrink-0 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-slate-900/95 sm:px-5 sm:py-4">
          <div className="flex flex-col gap-3 min-[430px]:flex-row min-[430px]:items-center min-[430px]:justify-between">
            <BrandLockup
              logoUrl={settings?.logo_url}
              platformName={platformName}
              platformTagline={platformTagline}
              size="sm"
              showTagline
              className="max-w-full"
            />
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300">
              <ShieldCheck className="h-3.5 w-3.5" />
              Age Gate
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 sm:h-11 sm:w-11">
              <ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300 sm:text-xs">
                Adults Only
              </p>
              <h2 id="age-consent-title" className="text-lg font-bold leading-tight sm:text-2xl">
                18+ Age Verification Required
              </h2>
            </div>
          </div>

          <p id="age-consent-description" className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
            {platformName} is intended only for adults. You must be at least 18 years old and legally permitted to access this platform in your location.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-2.5 min-[520px]:grid-cols-3 sm:gap-3">
            <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200 dark:bg-white/[0.04] dark:ring-0">
              <CalendarCheck className="mb-2 h-5 w-5 text-emerald-600 dark:text-emerald-300" aria-hidden="true" />
              <p className="text-xs font-semibold text-slate-900 dark:text-slate-100">18+ only</p>
              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">Adult access confirmation is required.</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200 dark:bg-white/[0.04] dark:ring-0">
              <Lock className="mb-2 h-5 w-5 text-emerald-600 dark:text-emerald-300" aria-hidden="true" />
              <p className="text-xs font-semibold text-slate-900 dark:text-slate-100">Legal access</p>
              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">Use must be allowed in your jurisdiction.</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200 dark:bg-white/[0.04] dark:ring-0">
              <ShieldCheck className="mb-2 h-5 w-5 text-emerald-600 dark:text-emerald-300" aria-hidden="true" />
              <p className="text-xs font-semibold text-slate-900 dark:text-slate-100">Responsible use</p>
              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">By entering, you accept platform rules.</p>
            </div>
          </div>

          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5 transition hover:bg-slate-100 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.06] sm:p-4">
            <input
              type="checkbox"
              checked={confirmedAdult}
              onChange={(event) => setConfirmedAdult(event.target.checked)}
              className="mt-1 h-5 w-5 shrink-0 rounded border-slate-300 bg-white text-emerald-500 focus:ring-emerald-500 dark:border-white/20 dark:bg-slate-950"
            />
            <span className="text-sm leading-6 text-slate-700 dark:text-slate-200">
              I confirm that I am at least 18 years old and legally permitted to access and use {platformName}.
            </span>
          </label>

          <div className="mt-4 flex flex-col gap-2.5 min-[460px]:flex-row sm:gap-3">
            <button
              type="button"
              onClick={handleEnter}
              disabled={!confirmedAdult}
              className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-950/40 transition hover:from-emerald-400 hover:to-teal-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Enter {platformName}
            </button>
            <button
              type="button"
              onClick={handleExit}
              className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-white/15 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:bg-white/[0.08]"
            >
              Exit
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>

          <p className="mt-4 text-center text-[11px] leading-5 text-slate-500 dark:text-slate-500">
            This confirmation is saved on this browser for {AGE_CONSENT_TTL_DAYS} days unless your browser data is cleared.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgeConsentGate;
